import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { TipId } from "../../../constants/Enums";
import TIP_REGISTRY, { TipConfig } from "./tips-registry";
import SwallowCard from "./SwallowCard";

// ─── localStorage helpers ────────────────────────────────────────────────────

const STORAGE_PREFIX = "nine_tip_seen_";

function isSeen(tipId: TipId): boolean {
  try { return localStorage.getItem(STORAGE_PREFIX + tipId) === "10"; } catch { return false; }
}

function markSeen(tipId: TipId): void {
  try { localStorage.setItem(STORAGE_PREFIX + tipId, "1"); } catch { /* blocked */ }
}

// ─── Context (kept minimal — consumers only need dismiss if they want manual control) ──

interface SwallowTipContextValue {
  dismiss: (tipId: TipId) => void;
}

const SwallowTipContext = createContext<SwallowTipContextValue | undefined>(undefined);

export const useSwallowTipContext = (): SwallowTipContextValue => {
  const ctx = useContext(SwallowTipContext);
  if (!ctx) throw new Error("useSwallowTipContext must be inside SwallowTipProvider");
  return ctx;
};

// ─── Provider ────────────────────────────────────────────────────────────────

export const SwallowTipProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();

  const [dismissed, setDismissed] = useState<Set<TipId>>(() => {
    const seen = new Set<TipId>();
    Object.values(TipId).forEach((id) => { if (isSeen(id)) seen.add(id); });
    return seen;
  });

  const [activeTip, setActiveTip] = useState<TipConfig | null>(null);

  // After the anchor element glows, we need to trigger it once
  const anchorGlowRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback((tipId: TipId) => {
    markSeen(tipId);
    setDismissed((prev) => { const n = new Set(prev); n.add(tipId); return n; });
    setActiveTip(null);
  }, []);

  // On route change, decide which tip (if any) to show.
  // We only verify the anchor exists at this moment — the rect itself is read
  // fresh at swallow time inside SwallowCard, so mobile scroll / address-bar
  // collapse / layout shifts don't desync the destination.
  useEffect(() => {
    const candidate = TIP_REGISTRY.find(
      (t) => pathname.startsWith(t.routePrefix) && !dismissed.has(t.id)
    );
    if (!candidate) { setActiveTip(null); return; }

    const t = setTimeout(() => {
      const el = document.querySelector(candidate.anchorSelector);
      if (el) setActiveTip(candidate);
    }, 650);

    return () => clearTimeout(t);
  }, [pathname, dismissed]);

  // After dismissal completes, apply a glow ring to the anchor element
  const handleDismiss = useCallback((tip: TipConfig) => {
    // Re-query at dismiss time — rect may have shifted since mount
    const el = document.querySelector(tip.anchorSelector);
    if (el) {
      el.classList.add("swallow-anchor-glow");
      if (anchorGlowRef.current) clearTimeout(anchorGlowRef.current);
      anchorGlowRef.current = setTimeout(() => {
        el.classList.remove("swallow-anchor-glow");
      }, 800);
    }
    dismiss(tip.id);
  }, [dismiss]);

  return (
    <SwallowTipContext.Provider value={{ dismiss }}>
      {children}
      {activeTip && (
        <SwallowCard
          key={activeTip.id}
          icon={activeTip.icon}
          message={activeTip.message}
          anchorSelector={activeTip.anchorSelector}
          holdMs={activeTip.holdMs}
          onDismiss={() => handleDismiss(activeTip)}
        />
      )}
    </SwallowTipContext.Provider>
  );
};
